import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Divider,
  Paper,
  Alert,
  TextField,
  Collapse,
  Fade,
  Avatar
} from '@mui/material';
import {
  OpenInNew as OpenIcon,
  Link as LinkIcon,
  Clear as ClearIcon,
  ArrowBack as BackIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon
} from '@mui/icons-material';

import { useSearch } from '../hooks/useSearch';
import { useDashboard } from '../hooks/useDashboard';
import { SearchResult } from '../types';

const SearchResults: React.FC = () => {
  const { searchQuery, searchResults, localSearchResults, isSearching, clearSearch } = useSearch({
    debounceMs: 300,
    minQueryLength: 2,
    searchInGroups: true
  });
  const { config } = useDashboard();

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'relevance' | 'alphabetical' | 'group'>('relevance');

  // Use local search results for instant feedback, fallback to server results
  const displayResults = localSearchResults.length > 0 ? localSearchResults : searchResults;
  const filteredResults = displayResults.filter(result => 
    selectedGroups.length === 0 || selectedGroups.includes(result.group.id)
  );

  const sortedResults = [...filteredResults].sort((a, b) => {
    switch (sortBy) {
      case 'alphabetical':
        return a.item.title.localeCompare(b.item.title);
      case 'group':
        return a.group.name.localeCompare(b.group.name) || a.item.title.localeCompare(b.item.title);
      default: // relevance
        return 0; // Keep original order
    }
  });

  const handleTileClick = (result: SearchResult) => {
    if (result.type === 'tile') {
      window.open(result.item.url, result.item.openInNewTab ? '_blank' : '_self');
    }
  };

  const toggleGroupFilter = (groupId: string) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const availableGroups = config?.groups || [];

  const getMatchTypeLabel = (matchType: string) => {
    switch (matchType) {
      case 'title':
        return 'Title';
      case 'description':
        return 'Description';
      case 'url':
        return 'URL';
      default:
        return 'Match';
    }
  };

  const getMatchTypeColor = (matchType: string) => {
    switch (matchType) {
      case 'title':
        return 'primary';
      case 'description':
        return 'secondary';
      case 'url':
        return 'info';
      default:
        return 'default';
    }
  };

  if (!searchQuery) {
    return null;
  }

  return (
    <Box>
      {/* Search Header */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={clearSearch} size="small">
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6">
                Search Results for "{searchQuery}"
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isSearching ? 'Searching...' : `Found ${sortedResults.length} result${sortedResults.length !== 1 ? 's' : ''}`}
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearSearch}
            size="small"
          >
            Clear Search
          </Button>
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            startIcon={<FilterIcon />}
            endIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            variant="outlined"
            size="small"
          >
            Filters
          </Button>
          
          <TextField 
            select
            size="small"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 120 }}
          >
            <option value="relevance">Relevance</option>
            <option value="alphabetical">A-Z</option>
            <option value="group">By Group</option>
          </TextField>

          {selectedGroups.length > 0 && (
            <Button
              size="small"
              onClick={() => setSelectedGroups([])}
              color="secondary"
            >
              Clear Filters ({selectedGroups.length})
            </Button>
          )}
        </Box>

        <Collapse in={showAdvancedFilters}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CategoryIcon fontSize="small" />
              Filter by Groups
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availableGroups.map((group) => (
                <Chip
                  key={group.id}
                  label={group.name}
                  onClick={() => toggleGroupFilter(group.id)}
                  color={selectedGroups.includes(group.id) ? 'primary' : 'default'}
                  variant={selectedGroups.includes(group.id) ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    borderColor: group.color,
                    color: selectedGroups.includes(group.id) ? 'white' : group.color,
                    backgroundColor: selectedGroups.includes(group.id) ? group.color : 'transparent',
                    '&:hover': {
                      backgroundColor: selectedGroups.includes(group.id) ? group.color : `${group.color}20`,
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {/* Search Results */}
      {isSearching && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Searching your dashboard...
          </Typography>
        </Box>
      )}

      {!isSearching && sortedResults.length === 0 && searchQuery && (
        <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto' }}>
          <Typography variant="body1" gutterBottom>
            No results found for "{searchQuery}"
          </Typography>
          <Typography variant="body2">
            Try different keywords or check your spelling. You can search by:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0 }}>
            <li>Tile titles</li>
            <li>Tile descriptions</li>
            <li>URLs</li>
            <li>Group names</li>
          </Box>
        </Alert>
      )}

      {!isSearching && sortedResults.length > 0 && (
        <Fade in={!isSearching}>
          <Grid container spacing={3}>
            {sortedResults.map((result, index) => (
              <Grid item xs={12} sm={6} md={4} key={`${result.item.id}-${index}`}>
                <Card
                  elevation={2}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: (theme) => theme.shadows[6],
                      '& .open-icon': {
                        opacity: 1
                      }
                    },
                    border: 1,
                    borderColor: 'divider',
                  }}
                  onClick={() => handleTileClick(result)}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    {/* Tile Title and Avatar */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: result.item.color,
                          width: 40,
                          height: 40,
                          mr: 2,
                          fontSize: '1.2rem'
                        }}
                      >
                        {result.item.title[0].toUpperCase()}
                      </Avatar>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h6" 
                          component="h3"
                          gutterBottom
                          sx={{
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            color: result.item.color || 'text.primary',
                          }}
                        >
                          {result.item.title}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={result.group.name}
                            size="small"
                            sx={{
                              backgroundColor: result.group.color + '20',
                              color: result.group.color,
                              borderColor: result.group.color + '40',
                              fontSize: '0.75rem',
                            }}
                            variant="outlined"
                          />
                          <Chip
                            label={getMatchTypeLabel(result.matchType)}
                            size="small"
                            color={getMatchTypeColor(result.matchType) as any}
                            variant="outlined"
                          />
                        </Box>
                      </Box>

                      <IconButton 
                        className="open-icon"
                        sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                        size="small"
                      >
                        <OpenIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Description */}
                    {result.item.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.4
                        }}
                      >
                        {result.item.description}
                      </Typography>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {/* URL */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LinkIcon fontSize="small" color="action" />
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '100%',
                        }}
                      >
                        {result.item.url}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Fade>
      )}

      {/* Search Tips */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          💡 Tip: Use Ctrl+K to quickly open search, or press Escape to clear results
        </Typography>
      </Box>
    </Box>
  );
};

export default SearchResults;